import { Injectable } from '@angular/core';

export interface CachedPageDocument {
  docHash: string;
  pageNumber: number;
  hash: string;
  dataUrl: string;
  mimeType: string;
  expiresAt?: number;
}

@Injectable({ providedIn: 'root' })
export class DocumentPageCacheService {
  private readonly dbName = 'document-page-cache';
  private readonly storeName = 'pages';
  private readonly dbPromise: Promise<IDBDatabase> | null;

  constructor() {
    this.dbPromise = this.supportsIndexedDb() ? this.openDatabase() : null;
    void this.cleanupExpiredEntries();
  }

  async getPage(docHash: string, pageNumber: number) {
    const db = await this.dbPromise;
    if (!db) {
      return undefined;
    }
    const id = this.buildId(docHash, pageNumber);
    return await new Promise<CachedPageDocument | undefined>((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const request = store.get(id);
      request.onsuccess = () => {
        const result = request.result as CachedPageDocument | undefined;
        if (result?.expiresAt && result.expiresAt <= Date.now()) {
          store.delete(id);
          resolve(undefined);
          return;
        }
        resolve(result ?? undefined);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async setPage(entry: CachedPageDocument) {
    const db = await this.dbPromise;
    if (!db) {
      return;
    }
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      store.put({ ...entry, expiresAt: undefined, id: this.buildId(entry.docHash, entry.pageNumber) });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  async markDocumentForDeletion(docHash: string, delayMs: number) {
    const db = await this.dbPromise;
    if (!db) {
      return;
    }
    const expiresAt = Date.now() + delayMs;
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      let request: IDBRequest<IDBCursorWithValue | null>;
      if (store.indexNames.contains('docHash')) {
        const index = store.index('docHash');
        request = index.openCursor(IDBKeyRange.only(docHash));
      } else {
        request = store.openCursor();
      }
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const value = cursor.value as CachedPageDocument & { id: string };
          if (value.docHash === docHash) {
            value.expiresAt = expiresAt;
            cursor.update(value);
          }
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  async clearDeletionSchedule(docHash: string) {
    const db = await this.dbPromise;
    if (!db) {
      return;
    }
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      let request: IDBRequest<IDBCursorWithValue | null>;
      if (store.indexNames.contains('docHash')) {
        const index = store.index('docHash');
        request = index.openCursor(IDBKeyRange.only(docHash));
      } else {
        request = store.openCursor();
      }
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const value = cursor.value as CachedPageDocument & { id: string };
          if (value.docHash === docHash && value.expiresAt) {
            value.expiresAt = undefined;
            cursor.update(value);
          }
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  async listDocumentPageHashes(docHash: string) {
    const db = await this.dbPromise;
    if (!db) {
      return new Map<number, string>();
    }
    return await new Promise<Map<number, string>>((resolve, reject) => {
      const result = new Map<number, string>();
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      let request: IDBRequest<IDBCursorWithValue | null>;
      if (store.indexNames.contains('docHash')) {
        request = store.index('docHash').openCursor(IDBKeyRange.only(docHash));
      } else {
        request = store.openCursor();
      }
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const value = cursor.value as CachedPageDocument;
          if (value.docHash === docHash && (!value.expiresAt || value.expiresAt > Date.now())) {
            result.set(value.pageNumber, value.hash);
          }
          cursor.continue();
        } else {
          resolve(result);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  private buildId(docHash: string, pageNumber: number) {
    return `${docHash}:${pageNumber}`;
  }

  private supportsIndexedDb() {
    return typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';
  }

  private openDatabase() {
    return new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 2);
      request.onupgradeneeded = () => {
        const db = request.result;
        let store: IDBObjectStore;
        if (!db.objectStoreNames.contains(this.storeName)) {
          store = db.createObjectStore(this.storeName, { keyPath: 'id' });
        } else {
          store = request.transaction!.objectStore(this.storeName);
        }
        if (!store.indexNames.contains('docHash')) {
          store.createIndex('docHash', 'docHash', { unique: false });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private async cleanupExpiredEntries() {
    const db = await this.dbPromise;
    if (!db) {
      return;
    }
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const request = store.openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const value = cursor.value as CachedPageDocument;
          if (value.expiresAt && value.expiresAt <= Date.now()) {
            cursor.delete();
          }
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }
}
